import { useTranslation } from "react-i18next";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CustomerAnalyticsSection } from "@/domains/customer/customers/components/analytics/customer-analytics-section";
import type { CustomerDetailFragment } from "@/gql/graphql";
import { useConfig } from "@/hooks/configuration";
import { useDialogRouterSimple } from "@/lib/dialog/use-dialog-router-simple";
import { env } from "@/lib/env";
import { useCustomerDetailAppointments } from "../hooks/use-customer-detail-appointments";
import { CustomerAppointmentHistoryTable } from "./cards/customer-appointment-history-table";
import { CustomerLoyaltyCard } from "./cards/customer-loyalty-card";
import { CustomerOverviewTab } from "./cards/customer-overview-tab";
import { CustomerEditDialog } from "./dialogs/CustomerEditDialog";

interface CustomerDetailViewProps {
  customer: CustomerDetailFragment;
  onBack?: () => void;
  onEdit?: () => void;
  onCall?: () => void;
  onEmail?: () => void;
  onBookAppointment?: () => void;
  showMobileActions?: boolean;
}

export function CustomerDetailView({
  customer,
  onCall,
  onEmail,
  onBookAppointment,
}: CustomerDetailViewProps) {
  const { t } = useTranslation("customers");
  const {
    appointments,
    loading: appointmentsLoading,
    loadMore,
    hasMore,
  } = useCustomerDetailAppointments(customer.id);
  const { value: loyaltyEnabled = false } = useConfig("loyalty.enabled");
  const { openDialog } = useDialogRouterSimple();

  const handleAppointmentClick = (appointmentId: string) => {
    openDialog({ type: "appointment-detail", id: appointmentId });
  };

  // Define available tabs based on experimental flags and loyalty settings
  const availableTabs = [
    { value: "overview", label: t("detail.tabs.overview") },
    { value: "appointments", label: `${t("detail.tabs.appointments")} (${appointments.length})` },
    // Conditionally include rewards tab based on loyalty setting
    ...(loyaltyEnabled ? [{ value: "rewards", label: t("detail.tabs.rewards") }] : []),
    // Conditionally include analytics tab
    ...(env.flags.showExperimental
      ? [{ value: "analytics", label: t("detail.tabs.analytics") }]
      : []),
  ];

  return (
    <>
      <div className="space-y-6">
        {/* Tabbed Main Content */}
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className={`grid w-full grid-cols-${availableTabs.length}`}>
            {availableTabs.map((tab) => (
              <TabsTrigger key={tab.value} value={tab.value}>
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="overview" className="space-y-6 mt-6">
            <CustomerOverviewTab
              customer={customer}
              onCall={onCall}
              onEmail={onEmail}
              onBookAppointment={onBookAppointment}
            />
          </TabsContent>

          <TabsContent value="appointments" className="space-y-6 mt-6">
            <CustomerAppointmentHistoryTable
              appointments={appointments}
              pointRewards={(customer as any)?.pointRewards || []}
              loading={appointmentsLoading}
              onAppointmentClick={handleAppointmentClick}
              onLoadMore={loadMore}
              hasMore={hasMore}
            />
          </TabsContent>

          {loyaltyEnabled && (
            <TabsContent value="rewards" className="space-y-6 mt-6">
              <CustomerLoyaltyCard customer={customer} loading={appointmentsLoading} />
            </TabsContent>
          )}

          {env.flags.showExperimental && (
            <TabsContent value="analytics" className="space-y-6 mt-6">
              <CustomerAnalyticsSection customerId={customer.id} />
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Edit Customer Dialog */}
      <CustomerEditDialog customer={customer} />
    </>
  );
}
